import { Head, Link, useForm } from "@inertiajs/react"
import { ArrowLeft, Plus, Minus, HelpCircle, Coffee } from "lucide-react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import CvaAffectiveForm from "@/components/cva-affective-form"
import CvaCombinedForm from "@/components/cva-combined-form"
import CvaDescriptiveForm from "@/components/cva-descriptive-form"
import { cuppingsPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Slider } from "@/components/ui/slider"
import { Textarea } from "@/components/ui/textarea"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Badge } from "@/components/ui/badge"
import { DescriptorPicker } from "@/components/DescriptorModal"

interface Sample {
  id: number
  name: string
}

interface Cupping {
  id: number
  title: string
  cupping_form: string
  cupping_form_label: string
  samples: Sample[]
}

interface ExistingCupping {
  sample_id: number
  total_cups: number
  roast_level: string
  fragrance_score: number
  fragrance_dry: number
  fragrance_break: number
  acidity_score: number
  acidity_intensity: number
  body_score: number
  body_level: number
  flavor_score: number
  aftertaste_score: number
  balance_score: number
  overall_score: number
  uniformity_score: number
  clean_cup_score: number
  sweetness_score: number
  defects_cups: number
  defects_intensity: number
  re_roast: string
  notes: string
  aroma_sca_selections?: Record<string, string[]>
}

interface Props {
  cupping: Cupping
  existing_cupping?: ExistingCupping | null
}

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Sesiones de catación",
    href: cuppingsPath(),
  },
]

const roastLevels = [
  { label: "Claro", value: "light" },
  { label: "Medio-Claro", value: "mid-light" },
  { label: "Medio", value: "medium" },
  { label: "Medio-Oscuro", value: "mid-dark" },
  { label: "Oscuro", value: "dark" },
]

// Professional evaluation categories (full SCA protocol)
const professionalEvaluationCategories = [
  { key: "fragrance", label: "Fragancia / Aroma", hasIntensity: true },
  { key: "acidity", label: "Acidez", hasIntensity: true },
  { key: "body", label: "Cuerpo", hasIntensity: false },
  { key: "flavor", label: "Sabor", hasIntensity: false },
  { key: "aftertaste", label: "Retrogusto", hasIntensity: false },
  { key: "balance", label: "Balance", hasIntensity: false },
]

const professionalQualityAttributes = [
  { key: "uniformity", label: "Uniformidad" },
  { key: "clean_cup", label: "Taza limpia" },
  { key: "sweetness", label: "Dulzura" },
]

export default function CuppingSession({ cupping, existing_cupping }: Props) {
  // Use professional evaluation categories (full SCA protocol)
  const evaluationCategories = professionalEvaluationCategories
  const qualityAttributes = professionalQualityAttributes

  // Icon for cupping
  const CupIcon = Coffee

  const [currentSample, setCurrentSample] = useState(0)
  const [roastLevel, setRoastLevel] = useState(
    existing_cupping?.roast_level || "medium",
  )
  const [showSummaryModal, setShowSummaryModal] = useState(false)
  const [selectedDescriptors, setSelectedDescriptors] = useState<{
    [key: string]: string[]
  }>(
    existing_cupping?.aroma_sca_selections &&
      typeof existing_cupping.aroma_sca_selections === "object" &&
      !Array.isArray(existing_cupping.aroma_sca_selections)
      ? (existing_cupping.aroma_sca_selections as Record<string, string[]>)
      : {},
  )

  const { data, setData, post, processing } = useForm({
    sample_id: existing_cupping?.sample_id || cupping.samples[0]?.id,
    total_cups: existing_cupping?.total_cups || 5,
    roast_level: existing_cupping?.roast_level || "medium",
    fragrance_score: existing_cupping?.fragrance_score || 7.5,
    fragrance_dry: existing_cupping?.fragrance_dry || 0,
    fragrance_break: existing_cupping?.fragrance_break || 0,
    acidity_score: existing_cupping?.acidity_score || 7.5,
    acidity_intensity: existing_cupping?.acidity_intensity || 0,
    body_score: existing_cupping?.body_score || 7.5,
    body_level: existing_cupping?.body_level || 0,
    flavor_score: existing_cupping?.flavor_score || 7.5,
    aftertaste_score: existing_cupping?.aftertaste_score || 7.5,
    balance_score: existing_cupping?.balance_score || 7.5,
    overall_score: existing_cupping?.overall_score || 7.5,
    uniformity_score: existing_cupping?.uniformity_score || 6,
    clean_cup_score: existing_cupping?.clean_cup_score || 6,
    sweetness_score: existing_cupping?.sweetness_score || 6,
    defects_cups: existing_cupping?.defects_cups || 0,
    defects_intensity: existing_cupping?.defects_intensity || 0,
    re_roast: existing_cupping?.re_roast || "no",
    notes: existing_cupping?.notes || "",
    aroma_sca_selections:
      existing_cupping?.aroma_sca_selections ||
      ({} as Record<string, string[]>),
  })

  const handleScoreChange = (key: string, value: number[]) => {
    setData(key as any, value[0])
  }

  const calculateTotalScore = () => {
    const total =
      data.fragrance_score +
      data.acidity_score +
      data.body_score +
      data.flavor_score +
      data.aftertaste_score +
      data.balance_score +
      data.overall_score +
      data.uniformity_score +
      data.clean_cup_score +
      data.sweetness_score -
      (data.defects_cups * 2 + data.defects_intensity * 4)
    return total.toFixed(2)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setShowSummaryModal(true)
  }

  const handleSampleChange = (index: number) => {
    setCurrentSample(index)
    setData("sample_id", cupping.samples[index]?.id)
  }

  const handleConfirmSubmit = () => {
    // Check if cupping has samples
    if (!cupping.samples || cupping.samples.length === 0) {
      alert(
        "Error: Esta sesión de catación no tiene muestras asociadas. Por favor, agrega muestras a la sesión antes de realizar la evaluación.",
      )
      return
    }

    // Ensure sample_id is set
    const sampleId = data.sample_id || cupping.samples[currentSample]?.id

    if (!sampleId) {
      console.error("No sample_id available")
      console.error("Available samples:", cupping.samples)
      console.error("Current sample index:", currentSample)
      alert(
        "Error: No se pudo identificar la muestra. Por favor, recarga la página.",
      )
      return
    }

    // Update data if needed
    if (data.sample_id !== sampleId) {
      setData("sample_id", sampleId)
    }

    // Sync descriptors into form data before submitting
    data.aroma_sca_selections = selectedDescriptors
    post(`/cuppings/${cupping.id}/cup`)
  }

  const sample = cupping.samples[currentSample]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head
        title={`${existing_cupping ? "Editar" : ""} Sesión de catación #${cupping.id}`}
      />

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Link
              href={cuppingsPath()}
              className="text-muted-foreground hover:text-foreground mb-2 flex items-center gap-2 text-sm"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver a Sesiones de catación
            </Link>
            <h1 className="text-3xl font-bold">
              {existing_cupping ? "Editar" : ""} Sesión de catación #
              {cupping.id}
            </h1>
            <p className="text-muted-foreground text-sm">
              Usando actualmente{" "}
              <span className="text-primary font-medium">
                {cupping.cupping_form_label}
              </span>{" "}
              - Formato:{" "}
              <span className="text-primary font-medium">Profesional</span>
            </p>
          </div>
        </div>

        {cupping.cupping_form === "sca_cva_affective" ? (
          <CvaAffectiveForm
            cupping={cupping}
            existing_cupping={existing_cupping as any}
          />
        ) : cupping.cupping_form === "sca_cva_descriptive" ? (
          <CvaDescriptiveForm
            cupping={cupping}
            existing_cupping={existing_cupping as any}
          />
        ) : cupping.cupping_form === "sca_cva_combined" ? (
          <CvaCombinedForm
            cupping={cupping}
            existing_cupping={existing_cupping as any}
          />
        ) : (
          <>
            <Card>
              <CardContent className="p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Sample Selector */}
                  <div className="flex items-center gap-4 border-b pb-4">
                    <div className="flex items-center gap-2">
                      {cupping.samples.map((s: Sample, idx: number) => (
                        <Button
                          key={s.id}
                          type="button"
                          variant={
                            currentSample === idx ? "default" : "outline"
                          }
                          onClick={() => handleSampleChange(idx)}
                          className={currentSample === idx ? "bg-primary" : ""}
                        >
                          {idx + 1}
                        </Button>
                      ))}
                    </div>
                    <div className="flex-1" />
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Label className="text-sm">Nivel de tueste</Label>
                        <HelpCircle className="text-muted-foreground h-4 w-4" />
                      </div>
                    </div>
                  </div>

                  {/* Sample Info and Roast Level */}
                  <div className="grid gap-6 md:grid-cols-2">
                    <div>
                      <p className="text-muted-foreground mb-2 text-sm">
                        Muestra #{currentSample + 1}
                      </p>
                    </div>

                    {/* Roast Level Selector */}
                    <div className="flex items-center justify-end gap-2">
                      {roastLevels.map((level) => (
                        <button
                          key={level.value}
                          type="button"
                          onClick={() => {
                            setRoastLevel(level.value)
                            setData("roast_level", level.value)
                          }}
                          className={`flex flex-col items-center gap-1 rounded-lg p-2 transition-colors ${
                            roastLevel === level.value
                              ? "bg-primary/10"
                              : "hover:bg-gray-50"
                          }`}
                        >
                          <div
                            className={`h-10 w-10 rounded-full border-2 ${
                              roastLevel === level.value
                                ? "border-primary"
                                : "border-gray-300"
                            }`}
                            style={{
                              backgroundColor:
                                level.value === "light"
                                  ? "#D4A574"
                                  : level.value === "mid-light"
                                    ? "#B8865F"
                                    : level.value === "medium"
                                      ? "#8B5A3C"
                                      : level.value === "mid-dark"
                                        ? "#6B4423"
                                        : "#3E2723",
                            }}
                          />
                          <span className="text-xs">{level.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Evaluation Categories - Grid */}
                  <div className="grid gap-4 md:grid-cols-3">
                    {evaluationCategories.map((category) => (
                      <Card key={category.key}>
                        <CardContent className="p-4">
                          <div className="space-y-4">
                            <h3 className="font-medium">{category.label}</h3>
                            <DescriptorPicker
                              selected={selectedDescriptors[category.key] || []}
                              onApply={(sel) =>
                                setSelectedDescriptors({
                                  ...selectedDescriptors,
                                  [category.key]: sel,
                                })
                              }
                              maxSelections={5}
                              title={`Descriptores - ${category.label}`}
                            />

                            {category.hasIntensity && (
                              <div className="space-y-2">
                                <div className="flex items-center justify-between text-sm">
                                  <span>Seco</span>
                                  <span>
                                    {(data[
                                      `${category.key}_dry` as keyof typeof data
                                    ] as number) || 0}
                                  </span>
                                  <span>Quiebre</span>
                                  <span>
                                    {(data[
                                      `${category.key}_break` as keyof typeof data
                                    ] as number) || 0}
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <Slider
                                    value={[
                                      (data[
                                        `${category.key}_dry` as keyof typeof data
                                      ] as number) || 0,
                                    ]}
                                    onValueChange={(value) =>
                                      handleScoreChange(
                                        `${category.key}_dry`,
                                        value,
                                      )
                                    }
                                    max={10}
                                    step={0.25}
                                    className="w-full"
                                  />
                                  <Slider
                                    value={[
                                      (data[
                                        `${category.key}_break` as keyof typeof data
                                      ] as number) || 0,
                                    ]}
                                    onValueChange={(value) =>
                                      handleScoreChange(
                                        `${category.key}_break`,
                                        value,
                                      )
                                    }
                                    max={10}
                                    step={0.25}
                                    className="w-full"
                                  />
                                </div>
                              </div>
                            )}

                            {category.key === "acidity" && (
                              <div className="space-y-2">
                                <div className="flex items-center justify-between text-sm">
                                  <span>Intensidad de Acidez</span>
                                  <span>{data.acidity_intensity}</span>
                                </div>
                                <Slider
                                  value={[data.acidity_intensity]}
                                  onValueChange={(value) =>
                                    handleScoreChange(
                                      "acidity_intensity",
                                      value,
                                    )
                                  }
                                  max={10}
                                  step={0.25}
                                  className="w-full"
                                />
                              </div>
                            )}

                            {category.key === "body" && (
                              <div className="space-y-2">
                                <div className="flex items-center justify-between text-sm">
                                  <span>Nivel de Cuerpo</span>
                                  <span>{data.body_level}</span>
                                </div>
                                <Slider
                                  value={[data.body_level]}
                                  onValueChange={(value) =>
                                    handleScoreChange("body_level", value)
                                  }
                                  max={10}
                                  step={0.25}
                                  className="w-full"
                                />
                              </div>
                            )}

                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium">
                                Puntuación
                              </span>
                              <div className="flex items-center gap-2">
                                <Button
                                  type="button"
                                  size="icon"
                                  variant="outline"
                                  className="bg-primary hover:bg-primary/90 h-8 w-8 rounded-full text-white"
                                  onClick={() => {
                                    const currentScore = data[
                                      `${category.key}_score` as keyof typeof data
                                    ] as number
                                    setData(
                                      `${category.key}_score` as any,
                                      Math.max(0, currentScore - 0.25),
                                    )
                                  }}
                                >
                                  <Minus className="h-4 w-4" />
                                </Button>
                                <span className="w-12 text-center font-semibold">
                                  {String(
                                    data[
                                      `${category.key}_score` as keyof typeof data
                                    ],
                                  )}
                                </span>
                                <Button
                                  type="button"
                                  size="icon"
                                  variant="outline"
                                  className="bg-primary hover:bg-primary/90 h-8 w-8 rounded-full text-white"
                                  onClick={() => {
                                    const currentScore = data[
                                      `${category.key}_score` as keyof typeof data
                                    ] as number
                                    setData(
                                      `${category.key}_score` as any,
                                      Math.min(10, currentScore + 0.25),
                                    )
                                  }}
                                >
                                  <Plus className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Bottom Section - 3 Columns */}
                  <div className="grid gap-4 md:grid-cols-3">
                    {/* Overall Score */}
                    <Card>
                      <CardContent className="p-4">
                        <div className="space-y-4">
                          <h3 className="font-medium">General</h3>
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">
                              Puntuación
                            </span>
                            <div className="flex items-center gap-2">
                              <Button
                                type="button"
                                size="icon"
                                variant="outline"
                                className="bg-primary hover:bg-primary/90 h-8 w-8 rounded-full text-white"
                                onClick={() =>
                                  setData(
                                    "overall_score",
                                    Math.max(0, data.overall_score - 0.25),
                                  )
                                }
                              >
                                <Minus className="h-4 w-4" />
                              </Button>
                              <span className="w-12 text-center font-semibold">
                                {data.overall_score}
                              </span>
                              <Button
                                type="button"
                                size="icon"
                                variant="outline"
                                className="bg-primary hover:bg-primary/90 h-8 w-8 rounded-full text-white"
                                onClick={() =>
                                  setData(
                                    "overall_score",
                                    Math.min(10, data.overall_score + 0.25),
                                  )
                                }
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>

                          {/* Defects - Professional Format */}
                          {
                            <div className="space-y-2 border-t pt-4">
                              <h4 className="font-medium">Defectos</h4>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-primary"
                              >
                                <Plus className="mr-1 h-3 w-3" />
                                Agregar descriptores
                              </Button>
                              <p className="text-muted-foreground text-xs italic">
                                Aún no se ha agregado descriptor, haz clic en
                                agregar descriptor para añadir
                              </p>
                            </div>
                          }
                        </div>
                      </CardContent>
                    </Card>

                    {/* Quality Attributes - Professional Format */}
                    {
                      <Card>
                        <CardContent className="p-4">
                          <div className="space-y-3">
                            <h3 className="font-medium">
                              Atributos de Calidad
                            </h3>
                            {qualityAttributes.map((attr) => {
                              const currentScore = data[
                                `${attr.key}_score` as keyof typeof data
                              ] as number
                              const selectedCups = Math.round(currentScore / 2)
                              return (
                                <div
                                  key={attr.key}
                                  className="flex items-center justify-between gap-2"
                                >
                                  <span className="w-20 shrink-0 text-xs font-medium">
                                    {attr.label}
                                  </span>
                                  <div className="flex items-center gap-0.5">
                                    {[1, 2, 3, 4, 5].map((cup) => (
                                      <button
                                        key={cup}
                                        type="button"
                                        onClick={() =>
                                          setData(
                                            `${attr.key}_score` as any,
                                            cup * 2,
                                          )
                                        }
                                        className={`flex h-7 w-7 items-center justify-center rounded-full transition-colors ${
                                          cup <= selectedCups
                                            ? "bg-primary hover:bg-primary/90 text-white"
                                            : "bg-muted text-muted-foreground hover:bg-muted/80"
                                        }`}
                                      >
                                        <CupIcon className="h-3.5 w-3.5" />
                                      </button>
                                    ))}
                                  </div>
                                  <span className="w-6 text-right text-sm font-semibold">
                                    {currentScore}
                                  </span>
                                </div>
                              )
                            })}
                          </div>
                        </CardContent>
                      </Card>
                    }

                    {/* Notes */}
                    <Card>
                      <CardContent className="p-4">
                        <div className="space-y-3">
                          <h3 className="font-medium">Notas</h3>
                          <Textarea
                            value={data.notes}
                            onChange={(
                              e: React.ChangeEvent<HTMLTextAreaElement>,
                            ) => setData("notes", e.target.value)}
                            placeholder="Escribe aquí"
                            rows={10}
                            className="resize-none"
                          />
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      disabled={processing}
                      size="lg"
                      className="bg-primary hover:bg-primary/90"
                    >
                      Revisar y Enviar
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Summary Modal */}
            <Dialog open={showSummaryModal} onOpenChange={setShowSummaryModal}>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="text-2xl">
                    Resumen de Evaluación de Catación
                  </DialogTitle>
                  <DialogDescription>
                    Revisa tus puntuaciones de catación antes de enviar
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                  {/* Sample Info */}
                  <div className="bg-primary/10 rounded-lg p-4">
                    <h3 className="mb-2 font-semibold">
                      Información de Muestra
                    </h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">Muestra:</span>
                        <span className="ml-2 font-medium">
                          {sample?.name || `Muestra #${currentSample + 1}`}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">
                          Nivel de Tueste:
                        </span>
                        <span className="ml-2 font-medium capitalize">
                          {roastLevel}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">
                          Total de Tazas:
                        </span>
                        <span className="ml-2 font-medium">
                          {data.total_cups}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Scores Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <h4 className="text-sm font-semibold">
                        Puntuaciones de Evaluación
                      </h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span>Fragancia/Aroma:</span>
                          <span className="font-semibold">
                            {data.fragrance_score}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Acidez:</span>
                          <span className="font-semibold">
                            {data.acidity_score}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Cuerpo:</span>
                          <span className="font-semibold">
                            {data.body_score}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Sabor:</span>
                          <span className="font-semibold">
                            {data.flavor_score}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Retrogusto:</span>
                          <span className="font-semibold">
                            {data.aftertaste_score}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Balance:</span>
                          <span className="font-semibold">
                            {data.balance_score}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>General:</span>
                          <span className="font-semibold">
                            {data.overall_score}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h4 className="text-sm font-semibold">
                        Atributos de Calidad
                      </h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span>Uniformidad:</span>
                          <span className="font-semibold">
                            {data.uniformity_score}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Taza Limpia:</span>
                          <span className="font-semibold">
                            {data.clean_cup_score}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Dulzura:</span>
                          <span className="font-semibold">
                            {data.sweetness_score}
                          </span>
                        </div>
                      </div>

                      <div className="border-t pt-2">
                        <h4 className="mb-1 text-sm font-semibold">Defectos</h4>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span>#Cups:</span>
                            <span className="font-semibold">
                              {data.defects_cups}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Intensidad:</span>
                            <span className="font-semibold">
                              {data.defects_intensity}
                            </span>
                          </div>
                          <div className="text-destructive flex justify-between">
                            <span>Deducción:</span>
                            <span className="font-semibold">
                              -
                              {(
                                data.defects_cups * 2 +
                                data.defects_intensity * 4
                              ).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Total Score */}
                  <div className="bg-primary rounded-lg p-4 text-white">
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-semibold">
                        Puntuación Total:
                      </span>
                      <span className="text-3xl font-bold">
                        {calculateTotalScore()}
                      </span>
                    </div>
                  </div>

                  {/* Notes */}
                  {data.notes && (
                    <div>
                      <h4 className="mb-1 text-sm font-semibold">Notas</h4>
                      <p className="text-muted-foreground rounded bg-gray-50 p-3 text-sm">
                        {data.notes}
                      </p>
                    </div>
                  )}

                  {/* Descriptors */}
                  {Object.keys(selectedDescriptors).length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold">Descriptores</h4>
                      {Object.entries(selectedDescriptors).map(
                        ([categoryKey, descriptors]) => {
                          const category = evaluationCategories.find(
                            (c) => c.key === categoryKey,
                          )
                          if (!descriptors || descriptors.length === 0)
                            return null
                          return (
                            <div key={categoryKey} className="space-y-2">
                              <p className="text-muted-foreground text-xs font-medium">
                                {category?.label}
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {descriptors.map((descriptor, idx) => (
                                  <Badge
                                    key={idx}
                                    variant="secondary"
                                    className="bg-primary/10 text-primary"
                                  >
                                    {descriptor}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )
                        },
                      )}
                    </div>
                  )}
                </div>

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowSummaryModal(false)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="button"
                    onClick={handleConfirmSubmit}
                    disabled={processing}
                    className="bg-primary hover:bg-primary/90"
                  >
                    {processing ? "Enviando..." : "Confirmar y Enviar"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>
    </AppLayout>
  )
}
